import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import { useSplitEnabled } from '@buffer-mono/features'

import { FilterByChannel_Channel } from '~publish/components/FilterByChannel'
import { buildTagComparator } from '~publish/components/FilterByTag'
import {
  useIsFreeUser,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { sanitizeNullableArray } from '~publish/helpers/typeGuards'
import {
  parseQueryParams,
  serializeQueryParams,
  useQueryParam,
} from '~publish/hooks/useQueryParam'

import type { InsightsFilterInput, PostType } from '../graphqlEnums'
import { buildInsightsFilterInput } from '../hooks/buildInsightsFilterInput'
import { useComparisonSelector } from '../hooks/useComparisonSelector'
import { parsePostTypes } from '~publish/helpers/postType'
import type { FilterablePostType } from '~publish/helpers/postType'
import { POST_TYPE_FILTER_SPLIT } from '~publish/helpers/postType'

import type { ComparisonMode } from '../utils'
import { useDateRangeSelector } from '../hooks/useDateRangeSelector'
import { useInsightsCustomDateUpgrade } from '../hooks/useInsightsCustomDateUpgrade'

// All viewable channels for the org, shared across the page's graphs/filters.
// `...FilterByChannel_Channel` carries the metadata the channel filter needs.
const GetInsightsChannels = graphql(
  /* GraphQL */ `
    query GetInsightsChannels {
      account {
        id
        currentOrganization {
          id
          channels(input: { filter: { product: publish, isLocked: false } }) {
            id
            service
            type
            allowedActions
            postingGoal {
              goal
            }
            postingSchedule {
              times
              paused
            }
            ...FilterByChannel_Channel
          }
        }
      }
    }
  `,
  [FilterByChannel_Channel],
)

// ========================================
// Types
// ========================================

/** A single channel as returned by {@link GetInsightsChannels}. */
export type InsightsChannel = NonNullable<
  NonNullable<
    ResultOf<typeof GetInsightsChannels>['account']['currentOrganization']
  >['channels'][number]
>

// Determines which page is active: AllChannelsInsightsPage passes no channelId
// (allChannels), InsightsChannel passes one (channel). Hooks like
// useInsightsFilterInput use this to apply the right default channelIds.
type InsightsScope =
  | { type: 'allChannels' }
  | { type: 'channel'; channelId: string }

type InsightsPageContextValue = {
  // Feature flags
  shouldDisplayUpgradePath: boolean
  // Primary date range
  startDate: Date
  endDate: Date
  selectedDateRange: string
  onDateRangeChange: (value: string) => void
  onCustomRangeApply: (start: Date, end: Date) => void
  // Comparison date range
  comparisonMode: ComparisonMode
  comparisonStartDate?: Date
  comparisonEndDate?: Date
  onComparisonModeChange: (mode: ComparisonMode) => void
  onCustomComparisonApply: (start: Date, end: Date) => void
  // Tag filter
  selectedTagIds: string[]
  tagFilter: ReturnType<typeof buildTagComparator>
  setSelectedTagIds: (value: string[] | undefined) => void
  // Post type filter
  isPostTypeFilterEnabled: boolean
  selectedPostTypes: FilterablePostType[]
  setSelectedPostTypes: (value: string[] | undefined) => void
  clearPostFilters: () => void
  // Custom date upgrade dialog
  isCustomDateUpgradeOpen: boolean
  setIsCustomDateUpgradeOpen: (open: boolean) => void
  handleCustomBlocked: () => void
  handleCustomDateUpgradeClick: () => void
  // Scope
  scope: InsightsScope
  // Channels (all-channels scope only; empty on a single-channel page)
  channels: InsightsChannel[]
}

// ========================================
// Context Definition
// ========================================

export const InsightsPageContext =
  createContext<InsightsPageContextValue | null>(null)

// ========================================
// Provider Component
// ========================================

const EMPTY_TAG_IDS: string[] = []
const EMPTY_POST_TYPES: FilterablePostType[] = []

export type InsightsPageProviderProps = {
  /**
   * When present, the provider is scoped to a single channel.
   * Omit (or pass undefined) for the all-channels view.
   */
  channelId?: string
  children: ReactNode
}

export function InsightsPageProvider({
  channelId,
  children,
}: InsightsPageProviderProps): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const isFreeUser = useIsFreeUser()
  const shouldDisplayUpgradePath = isFreeUser

  const {
    startDate,
    endDate,
    selectedDateRange,
    onDateRangeChange,
    onCustomRangeApply,
  } = useDateRangeSelector()

  const {
    comparisonMode,
    comparisonStartDate,
    comparisonEndDate,
    onComparisonModeChange,
    onCustomComparisonApply,
  } = useComparisonSelector({ primaryStart: startDate, primaryEnd: endDate })

  const { handleUpgradeClick } = useInsightsCustomDateUpgrade()
  const [isCustomDateUpgradeOpen, setIsCustomDateUpgradeOpen] = useState(false)

  const handleCustomBlocked = useCallback(
    () => setIsCustomDateUpgradeOpen(true),
    [],
  )
  const handleCustomDateUpgradeClick = useCallback(() => {
    setIsCustomDateUpgradeOpen(false)
    handleUpgradeClick()
  }, [handleUpgradeClick])

  // Reset to default if a free user somehow lands on a range they can no longer
  // pick (e.g. a stale URL): 'custom' is gated behind the upgrade path, and
  // 'last-month' is hidden for the free tier (CORE-6225).
  useEffect(() => {
    if (
      shouldDisplayUpgradePath &&
      (selectedDateRange === 'custom' || selectedDateRange === 'last-month')
    ) {
      onDateRangeChange('last-30-days')
    }
  }, [shouldDisplayUpgradePath, selectedDateRange, onDateRangeChange])

  const [queryTagIds, setSelectedTagIds] = useQueryParam<string[]>('tags')
  const selectedTagIds = queryTagIds ?? EMPTY_TAG_IDS

  const tagFilter = useMemo(
    () => buildTagComparator(selectedTagIds),
    [selectedTagIds],
  )

  const scope = useMemo<InsightsScope>(
    () =>
      channelId ? { type: 'channel', channelId } : { type: 'allChannels' },
    [channelId],
  )

  const { isEnabled: isPostTypeFilterEnabled } = useSplitEnabled(
    POST_TYPE_FILTER_SPLIT,
  )
  const [queryPostTypes, setSelectedPostTypes] =
    useQueryParam<string[]>('postTypes')
  // Gate the selection, not only the control. A shared URL carries `postTypes`,
  // and neither a flagged-off session nor the all-channels page renders a
  // control to clear it with, so an ungated read would filter silently.
  const selectedPostTypes = useMemo(
    () =>
      isPostTypeFilterEnabled && scope.type === 'channel'
        ? parsePostTypes(queryPostTypes)
        : EMPTY_POST_TYPES,
    [isPostTypeFilterEnabled, scope.type, queryPostTypes],
  )

  // Clears the tag and post type filters in one navigation.
  //
  // Two `useQueryParam` setters cannot do this. Each closes over the query it
  // parsed at render time, so the second one rebuilds the search string from a
  // snapshot that still holds the key the first one removed, and puts it back.
  const clearPostFilters = useCallback(() => {
    const search = serializeQueryParams({
      ...parseQueryParams(location.search),
      tags: undefined,
      postTypes: undefined,
    })
    navigate({ ...location, search })
  }, [location, navigate])

  // Org channel list shared by the all-channels view's graphs + filters. Skipped
  // on the single-channel page (no org-wide picker there), so it adds no request.
  const organizationId = useOrganizationId()
  const { data: channelsData } = useQuery(GetInsightsChannels, {
    skip: channelId != null || !organizationId,
  })
  const channels = useMemo(
    () =>
      sanitizeNullableArray(
        channelsData?.account?.currentOrganization?.channels,
      ),
    [channelsData],
  )

  const contextValue: InsightsPageContextValue = useMemo(
    () => ({
      shouldDisplayUpgradePath,
      startDate,
      endDate,
      selectedDateRange,
      onDateRangeChange,
      onCustomRangeApply,
      comparisonMode,
      comparisonStartDate,
      comparisonEndDate,
      onComparisonModeChange,
      onCustomComparisonApply,
      selectedTagIds,
      tagFilter,
      setSelectedTagIds,
      isPostTypeFilterEnabled,
      selectedPostTypes,
      setSelectedPostTypes,
      clearPostFilters,
      isCustomDateUpgradeOpen,
      setIsCustomDateUpgradeOpen,
      handleCustomBlocked,
      handleCustomDateUpgradeClick,
      scope,
      channels,
    }),
    [
      shouldDisplayUpgradePath,
      startDate,
      endDate,
      selectedDateRange,
      onDateRangeChange,
      onCustomRangeApply,
      comparisonMode,
      comparisonStartDate,
      comparisonEndDate,
      onComparisonModeChange,
      onCustomComparisonApply,
      selectedTagIds,
      tagFilter,
      setSelectedTagIds,
      isPostTypeFilterEnabled,
      selectedPostTypes,
      setSelectedPostTypes,
      clearPostFilters,
      isCustomDateUpgradeOpen,
      setIsCustomDateUpgradeOpen,
      handleCustomBlocked,
      handleCustomDateUpgradeClick,
      scope,
      channels,
    ],
  )

  return (
    <InsightsPageContext.Provider value={contextValue}>
      {children}
    </InsightsPageContext.Provider>
  )
}

InsightsPageProvider.displayName = 'InsightsPageProvider'

// ========================================
// Broad Context Hook
// ========================================

export function useInsightsPageContext(): InsightsPageContextValue {
  const context = useContext(InsightsPageContext)
  if (context === null) {
    throw new Error(
      'useInsightsPageContext must be used within an InsightsPageProvider. ' +
        'Wrap your component tree with <InsightsPageProvider> to use this hook.',
    )
  }
  return context
}

// ========================================
// Narrow Selector Hooks
// ========================================

export function useInsightsDateRange(): {
  startDate: Date
  endDate: Date
  selectedDateRange: string
  onDateRangeChange: (value: string) => void
  onCustomRangeApply: (start: Date, end: Date) => void
} {
  const {
    startDate,
    endDate,
    selectedDateRange,
    onDateRangeChange,
    onCustomRangeApply,
  } = useInsightsPageContext()
  return {
    startDate,
    endDate,
    selectedDateRange,
    onDateRangeChange,
    onCustomRangeApply,
  }
}

export function useInsightsComparison(): {
  comparisonMode: ComparisonMode
  comparisonStartDate?: Date
  comparisonEndDate?: Date
  onComparisonModeChange: (mode: ComparisonMode) => void
  onCustomComparisonApply: (start: Date, end: Date) => void
} {
  const {
    comparisonMode,
    comparisonStartDate,
    comparisonEndDate,
    onComparisonModeChange,
    onCustomComparisonApply,
  } = useInsightsPageContext()
  return {
    comparisonMode,
    comparisonStartDate,
    comparisonEndDate,
    onComparisonModeChange,
    onCustomComparisonApply,
  }
}

export function useInsightsTags(): {
  selectedTagIds: string[]
  tagFilter: ReturnType<typeof buildTagComparator>
  setSelectedTagIds: (value: string[] | undefined) => void
} {
  const { selectedTagIds, tagFilter, setSelectedTagIds } =
    useInsightsPageContext()
  return { selectedTagIds, tagFilter, setSelectedTagIds }
}

export function useInsightsPostTypes(): {
  isPostTypeFilterEnabled: boolean
  selectedPostTypes: FilterablePostType[]
  setSelectedPostTypes: (value: string[] | undefined) => void
  clearPostFilters: () => void
} {
  const {
    isPostTypeFilterEnabled,
    selectedPostTypes,
    setSelectedPostTypes,
    clearPostFilters,
  } = useInsightsPageContext()
  return {
    isPostTypeFilterEnabled,
    selectedPostTypes,
    setSelectedPostTypes,
    clearPostFilters,
  }
}

export function useInsightsUpgradePath(): {
  shouldDisplayUpgradePath: boolean
  isCustomDateUpgradeOpen: boolean
  setIsCustomDateUpgradeOpen: (open: boolean) => void
  handleCustomBlocked: () => void
  handleCustomDateUpgradeClick: () => void
} {
  const {
    shouldDisplayUpgradePath,
    isCustomDateUpgradeOpen,
    setIsCustomDateUpgradeOpen,
    handleCustomBlocked,
    handleCustomDateUpgradeClick,
  } = useInsightsPageContext()
  return {
    shouldDisplayUpgradePath,
    isCustomDateUpgradeOpen,
    setIsCustomDateUpgradeOpen,
    handleCustomBlocked,
    handleCustomDateUpgradeClick,
  }
}

// Returns the channelId when on a single-channel page, undefined on all-channels.
export function useInsightsChannelId(): string | undefined {
  const { scope } = useInsightsPageContext()
  return scope.type === 'channel' ? scope.channelId : undefined
}

/**
 * The org's viewable channels for the all-channels view. The single source of
 * truth for channel metadata (name, service, …) so graphs and filters don't each
 * refetch it. Empty on a single-channel page.
 */
export function useInsightsChannels(): {
  channels: InsightsChannel[]
} {
  const { channels } = useInsightsPageContext()
  return { channels }
}

// ========================================
// Builder Hooks
// ========================================

/**
 * Builds an InsightsFilterInput from context-provided org, dates, and tags,
 * plus optional overrides for date range (e.g. comparison period for top posts)
 * and channelIds (e.g. the per-channel filter on the all-channels view).
 *
 * When no overrides are provided:
 * - Dates default to the primary selected range from context.
 * - Channel scope defaults to the channel from context scope (single-channel
 *   view) or no channel restriction (all-channels view).
 * - Post types default to the URL selection, which the context has already
 *   gated on the flag and the scope.
 *
 * `postTypes` is overridable because only the caller knows the channel's
 * service. A format the service cannot produce must not reach the query: the
 * control is hidden for that service, so the filter would narrow the page with
 * nothing to clear it. @see supportedPostTypesForService
 */
export function useInsightsFilterInput(overrides?: {
  startDate?: Date
  endDate?: Date
  channelIds?: string[]
  postTypes?: readonly PostType[]
}): InsightsFilterInput {
  const organizationId = useOrganizationId() ?? ''
  const { scope, startDate, endDate, tagFilter, selectedPostTypes } =
    useInsightsPageContext()

  const effectiveStartDate = overrides?.startDate ?? startDate
  const effectiveEndDate = overrides?.endDate ?? endDate
  const overrideChannelIds = overrides?.channelIds
  const effectivePostTypes = overrides?.postTypes ?? selectedPostTypes
  // Derive a primitive dep so the memo doesn't re-run on every render in channel scope
  const scopeChannelId = scope.type === 'channel' ? scope.channelId : undefined

  return useMemo(() => {
    const effectiveChannelIds =
      overrideChannelIds !== undefined
        ? overrideChannelIds
        : scopeChannelId !== undefined
          ? [scopeChannelId]
          : undefined
    return buildInsightsFilterInput({
      organizationId,
      startDate: effectiveStartDate,
      endDate: effectiveEndDate,
      channelIds: effectiveChannelIds,
      tags: tagFilter,
      postTypes: effectivePostTypes,
    })
  }, [
    organizationId,
    effectiveStartDate,
    effectiveEndDate,
    overrideChannelIds,
    scopeChannelId,
    tagFilter,
    effectivePostTypes,
  ])
}
